import React from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import SiteFooter from "@/components/SiteFooter";
import {
  ArrowRight, Layers, Type as TypeIcon, FileText, ScrollText,
  Cpu, ShieldCheck, Package,
} from "lucide-react";

// iter-61 — Public feature deep-dive at /features. Details the underlying
// formatter_v2 pipeline plus the Classic Trade typography spec, using the
// typographic-excellence mock card format the operator specified.

const CLASSIFIER_STEPS = [
  { title: "Label pattern dispatch",
    body: "High-confidence regex table catches Chapter/Part labels (digits, Roman numerals, and spelled-out numbers 1-99), named callouts (RN Insight, Bedside Example), and front/back-matter markers." },
  { title: "Word-style + length signals",
    body: "Heading 1-3 styles combined with a ≤12-word length ceiling classify short paragraphs as chapter titles or section headings — even when the label pattern misses." },
  { title: "Workbook signal detection",
    body: "Reflection prompts, exercises, and generic callouts caught by lead-phrase heuristics — before falling through to body paragraph." },
  { title: "Shape-based ad-hoc H3 promotion",
    body: "Short (2-8 words) title-case standalone paragraphs get promoted to SECTION_HEADING even when the source lacks any Word Heading styling." },
  { title: "Sequence pass",
    body: "Neighbour context refines borderline classifications, catching TOC blocks, back-matter transitions, and stray label ambiguity." },
  { title: "Auto-bullet promotion",
    body: "Body paragraphs ending in ':' followed by 2+ short single-sentence lines get promoted to BULLET_ITEM — 768 bullets promoted in a single 22-chapter manuscript run." },
];

export default function Features() {
  return (
    <div className="min-h-screen bg-[#faf9f6]">
      <Nav />

      {/* ─── Header ─────────────────────────────────────────────────── */}
      <section className="max-w-[1400px] mx-auto px-6 pt-16 pb-14">
        <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">
          The engine · formatter_v2
        </p>
        <h1
          className="font-editorial font-light tracking-tighter leading-[1.02] text-5xl sm:text-6xl lg:text-8xl mt-4"
          data-testid="features-h1"
        >
          Every paragraph, classified.
          <span className="block italic text-[color:var(--primary)]">
            Every page, composed.
          </span>
        </h1>
        <p className="mt-8 max-w-2xl text-lg leading-relaxed text-[color:var(--text-muted)]">
          Traditional formatters look at font names. BookForge360 looks at
          intent — running each paragraph through a six-stage semantic
          pipeline before a single glyph hits the page.
        </p>
      </section>

      {/* ─── Section 1 — Semantic classifier ─────────────────────────── */}
      <section
        className="border-t border-[color:var(--border)] bg-white"
        data-testid="features-classifier"
      >
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="grid grid-cols-12 gap-10 items-start">
            <div className="col-span-12 lg:col-span-5">
              <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">
                § 01 — The classifier
              </p>
              <h2 className="font-editorial text-4xl sm:text-5xl leading-tight mt-3">
                The dynamic semantic classifier.
              </h2>
              <p className="mt-6 text-[color:var(--text-muted)] leading-relaxed">
                Six stages, each falling through to the next if inconclusive.
                By the time the renderer sees a block, it has been evaluated
                against label patterns, Word styles, workbook heuristics,
                shape signals, sequence context, and colon-intro list runs.
              </p>
              <div className="mt-8 flex items-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[color:var(--primary)] text-white flex items-center justify-center">
                  <Cpu className="w-6 h-6" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="font-editorial text-2xl">25+ roles</div>
                  <div className="text-xs uppercase tracking-[0.15em] text-[color:var(--text-muted)]">
                    Distinct block classifications
                  </div>
                </div>
              </div>
            </div>
            <div className="col-span-12 lg:col-span-7">
              <ol className="space-y-3">
                {CLASSIFIER_STEPS.map((s, idx) => (
                  <li
                    key={s.title}
                    className="flex gap-5 p-5 bg-[#faf9f6] border border-[color:var(--border)] transition-all duration-300 hover:border-[color:var(--text)]"
                    data-testid={`classifier-step-${idx}`}
                  >
                    <span className="font-editorial text-4xl text-[color:var(--primary)] leading-none">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    <div>
                      <div className="font-editorial text-xl">{s.title}</div>
                      <p className="text-sm text-[color:var(--text-muted)] mt-1 leading-relaxed">
                        {s.body}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 2 — Typographic excellence card (mock book page) ─ */}
      <section
        className="border-t border-[color:var(--border)]"
        data-testid="features-typography"
      >
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="grid grid-cols-12 gap-10 items-center">
            <div className="col-span-12 lg:col-span-5">
              <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">
                § 02 — Typography spec
              </p>
              <h2 className="font-editorial text-4xl sm:text-5xl leading-tight mt-3">
                Classic Trade Standard.
              </h2>
              <ul className="mt-8 space-y-4 text-[color:var(--text-muted)]">
                <li className="flex gap-3">
                  <TypeIcon className="w-5 h-5 mt-1 text-[color:var(--primary)] shrink-0" strokeWidth={1.5} />
                  <span><strong className="text-[color:var(--text)]">11pt body</strong> in Crimson Pro / Georgia serif — a balanced 1.4 line-height, fully justified.</span>
                </li>
                <li className="flex gap-3">
                  <ScrollText className="w-5 h-5 mt-1 text-[color:var(--primary)] shrink-0" strokeWidth={1.5} />
                  <span><strong className="text-[color:var(--text)]">1.25em first-line indents</strong> on every body paragraph — dropped entirely after headings for a clean visual reset.</span>
                </li>
                <li className="flex gap-3">
                  <Layers className="w-5 h-5 mt-1 text-[color:var(--primary)] shrink-0" strokeWidth={1.5} />
                  <span><strong className="text-[color:var(--text)]">Widow/orphan protection</strong> keeps trailing lines from separating onto their own page.</span>
                </li>
                <li className="flex gap-3">
                  <FileText className="w-5 h-5 mt-1 text-[color:var(--primary)] shrink-0" strokeWidth={1.5} />
                  <span><strong className="text-[color:var(--text)]">24pt bold chapter titles</strong>, centered with a forced page break — 16pt bold section headings with 12pt above / 6pt below.</span>
                </li>
              </ul>
            </div>

            {/* Mock book page — typographic excellence card */}
            <div className="col-span-12 lg:col-span-7">
              <div
                className="bg-white border border-[color:var(--border)] shadow-[0_20px_60px_-30px_rgba(0,0,0,0.25)] p-10 sm:p-14 max-w-2xl mx-auto"
                style={{ fontFamily: "'Cormorant Garamond', 'Georgia', serif" }}
                data-testid="typography-mock-card"
              >
                <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--text-muted)] text-center mb-8">
                  Your Nervous System Is Stuck
                </div>
                <h3
                  className="text-center text-3xl font-bold mb-6"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Chapter 1
                </h3>
                <h4
                  className="text-left text-lg font-bold mt-6 mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  Why Your Symptoms Make Sense
                </h4>
                <p className="text-[11pt] leading-[1.4] text-justify text-[#1a1a1a]">
                  This is how a masterfully formatted page looks. The first
                  paragraph drops indent entirely after headings — a classic
                  trade convention that gives readers a clean visual reset
                  at every section break.
                </p>
                <p className="text-[11pt] leading-[1.4] text-justify text-[#1a1a1a] indent-[1.25em] mt-0">
                  Every paragraph following has a 1.25em first-line
                  indentation applied seamlessly by the compiler. Line height
                  sits at a golden 1.4 ratio for optimal readability across
                  print and screen.
                </p>
                <aside
                  className="mt-6 bg-[#f4f4f2] border-l-4 border-[#444] pl-4 pr-4 py-3 italic text-[10.5pt] leading-[1.4] text-[#222]"
                  data-testid="typography-mock-callout"
                >
                  <div className="uppercase tracking-[0.06em] font-bold not-italic text-[10pt] text-[#333] mb-2">
                    RN Insight
                  </div>
                  The body releases protective tension only when it feels
                  safe. Naming that reality is the first act of healing.
                </aside>
                <p className="text-[11pt] leading-[1.4] text-justify text-[#1a1a1a] indent-[1.25em] mt-4">
                  After the callout, the body prose continues with its
                  standard indent — the aside floats inside the reading flow
                  without breaking rhythm.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Section 3 — Container callouts ─────────────────────────── */}
      <section
        className="border-t border-[color:var(--border)] bg-white"
        data-testid="features-callouts"
      >
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="max-w-3xl">
            <p className="uppercase text-xs tracking-[0.25em] text-[color:var(--text-muted)]">
              § 03 — Container callouts
            </p>
            <h2 className="font-editorial text-4xl sm:text-5xl leading-tight mt-3">
              High-visibility <em className="text-[color:var(--primary)]">&lt;aside&gt;</em> containers.
            </h2>
            <p className="mt-6 text-[color:var(--text-muted)] leading-relaxed">
              RN Insights, Bedside Examples, and any custom named callout
              wrap into styled aside blocks with soft grey backdrops and
              elegant left-border rules. Page-break-inside: avoid keeps
              them from splitting mid-container across page turns.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: ShieldCheck, title: "Guarded from splits",
                body: "page-break-inside: avoid keeps every callout intact across page turns in the PDF export." },
              { icon: Package, title: "Portable across formats",
                body: "The same aside renders as a Quote-styled paragraph in DOCX and a themed div in EPUB — one source, three formats." },
              { icon: Layers, title: "Composable containment",
                body: "Bullet lists, sub-headings, and inline images all render inside the callout body while staying inside the aside." },
            ].map((c) => (
              <div
                key={c.title}
                className="bg-[#faf9f6] p-7 border border-[color:var(--border)]"
              >
                <c.icon
                  className="w-5 h-5 mb-4 text-[color:var(--primary)]"
                  strokeWidth={1.5}
                />
                <h3 className="font-editorial text-2xl mb-2">{c.title}</h3>
                <p className="text-sm text-[color:var(--text-muted)] leading-relaxed">
                  {c.body}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-14 text-center">
            <Link
              to="/register"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="features-cta"
            >
              Start with a free upload <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
